import type { ResolvedFigure } from '../model.js';
import { box, point, SceneBuilder, validateViewport } from '../scene-builder.js';
import { finishPrimitive, type PrimitiveSpec } from './contract.js';
import { d, fits, type Frame, frameFor, header, note, textWidth, TYPE } from './kit.js';

export interface ProportionPart {
  id: string;
  label: string;
  /** The measured share, in the same unit as every other part. */
  value: number;
  /** The author's own notation for the value, if the number needs one. */
  amount?: string;
}

export interface ProportionStripSpec extends PrimitiveSpec {
  family: 'proportion-strip';
  view: 'orthographic';
  subject: string;
  unit: string;
  /** Ordered largest-first or in the author's meaningful order; drawn in order. */
  parts: ProportionPart[];
  /** The whole. Must equal the sum of the parts exactly. */
  total: number;
  /** Contiguous parts at the end of the strip, re-drawn at true magnification. */
  magnifyPartIds?: string[];
  title: string;
  subtitle: string;
  focusPartId?: string;
}

export const proportionPartId = (id: string): string => `proportion.part.${id}`;
export const PROPORTION_WHOLE_ID = 'proportion.whole';
export const PROPORTION_MAGNIFIER_ID = 'proportion.magnifier';

interface Metrics {
  frame: Frame;
  stripX: number;
  stripW: number;
  rulerY: number;
  stripY: number;
  stripH: number;
  bracketY: number;
  detailTop: number;
  detailH: number;
  detailLabelY: number;
  footerY: number;
  required: number;
}

function metrics(spec: ProportionStripSpec): Metrics {
  const frame = frameFor(spec.viewport.width, spec.viewport.height);
  const stripX = frame.margin;
  const stripW = frame.contentWidth;
  const rulerY = frame.top + 22;
  const stripY = rulerY + 12;
  const stripH = 56;
  const bracketY = stripY + stripH + 26;
  const detailTop = bracketY + 44;
  const detailH = 48;
  const detailLabelY = detailTop + detailH + 20;
  const footerY = detailLabelY + 30;
  return { frame, stripX, stripW, rulerY, stripY, stripH, bracketY, detailTop, detailH, detailLabelY, footerY, required: footerY + 14 };
}

/**
 * A proportion strip is a promise about area.
 *
 * Its one rule is that every part's drawn width is its true share of the whole,
 * which is exactly why small parts are a problem: at true scale, 0.04 % of a
 * phone screen is a third of a pixel. The dishonest fixes are to round it up, to
 * break the axis, or to switch to a chart where size no longer means anything.
 * The honest fix is a magnifier: keep the strip exact, then re-draw a declared
 * slice of it at a stated magnification. This compiler computes that factor from
 * the data, so the enlargement can never quietly disagree with the strip above.
 */
export function compileProportionStrip(spec: ProportionStripSpec): ResolvedFigure {
  validateProportionStrip(spec);

  const builder = new SceneBuilder();
  const m = metrics(spec);
  const { frame } = m;
  const warnings: string[] = [];
  const scale = m.stripW / spec.total;
  const magnified = spec.magnifyPartIds ?? [];
  const magnifiedValue = spec.parts.filter((part) => magnified.includes(part.id)).reduce((sum, part) => sum + part.value, 0);
  const factor = magnified.length ? spec.total / magnifiedValue : 1;

  header(builder, frame, 'proportion', `COMPOSITION · ${spec.subject.toUpperCase()}`, [
    `WHOLE = ${format(spec.total)} ${spec.unit} · WIDTH IS SHARE, ALWAYS`,
    `WHOLE = ${format(spec.total)} ${spec.unit}`,
  ]);

  // The cumulative ruler: this axis is what makes width readable as share.
  for (let tick = 0; tick <= 4; tick += 1) {
    const fraction = tick / 4;
    const x = m.stripX + m.stripW * fraction;
    builder.line(`proportion.ruler.${tick}`, 'proportion.ruler', 'axis', point(x, m.rulerY), point(x, m.rulerY + 6));
    builder.text(`proportion.ruler.${tick}.label`, 'proportion.ruler', 'annotation', point(x, m.rulerY - 5), format(spec.total * fraction), {
      size: TYPE.micro,
      mono: true,
      weight: 500,
      align: tick === 0 ? 'start' : tick === 4 ? 'end' : 'middle',
    });
  }
  builder.anchor('proportion.ruler.origin', 'proportion.ruler', point(m.stripX, m.rulerY), 'left');
  builder.hit('proportion.ruler', box(m.stripX, m.rulerY - 16, m.stripW, 24));

  builder.rect(`${PROPORTION_WHOLE_ID}.frame`, PROPORTION_WHOLE_ID, 'structure', box(m.stripX, m.stripY, m.stripW, m.stripH), { radius: 3 });
  builder.anchor(`${PROPORTION_WHOLE_ID}.start`, PROPORTION_WHOLE_ID, point(m.stripX, m.stripY + m.stripH / 2), 'left');
  builder.anchor(`${PROPORTION_WHOLE_ID}.end`, PROPORTION_WHOLE_ID, point(m.stripX + m.stripW, m.stripY + m.stripH / 2), 'right');
  builder.hit(PROPORTION_WHOLE_ID, box(m.stripX, m.stripY, m.stripW, m.stripH));

  let cursor = m.stripX;
  const spans = new Map<string, { x: number; width: number }>();
  spec.parts.forEach((part, index) => {
    const semanticId = proportionPartId(part.id);
    const width = part.value * scale;
    const bounds = box(cursor, m.stripY, width, m.stripH);
    spans.set(part.id, { x: cursor, width });
    const active = part.id === spec.focusPartId;
    builder.rect(`${semanticId}.band`, semanticId, active ? 'active' : index % 2 === 0 ? 'surface' : 'record', bounds, {
      label: `${part.label}: ${format(part.value)} of ${format(spec.total)} ${spec.unit}`,
      interactive: true,
    });
    if (index > 0) builder.line(`${semanticId}.edge`, semanticId, 'structure', point(cursor, m.stripY), point(cursor, m.stripY + m.stripH));

    const share = `${format(part.value)} ${spec.unit}`;
    const room = width - 8;
    const labelSize = fits(part.label, TYPE.label, room, true) ? TYPE.label : TYPE.micro;
    if (fits(part.label, labelSize, room, true) && fits(share, TYPE.micro, room, true)) {
      builder.text(`${semanticId}.label`, semanticId, 'structure', point(cursor + width / 2, m.stripY + 26), part.label, {
        size: labelSize,
        mono: true,
        weight: 700,
        align: 'middle',
      });
      builder.text(`${semanticId}.share`, semanticId, 'annotation', point(cursor + width / 2, m.stripY + 43), share, {
        size: TYPE.micro,
        mono: true,
        weight: 500,
        align: 'middle',
      });
    } else if (!magnified.includes(part.id)) {
      warnings.push(`${part.label} is too narrow to label inside the strip and is not covered by the magnifier.`);
    }
    builder.anchor(`${semanticId}.start`, semanticId, point(cursor, m.stripY), 'up');
    builder.anchor(`${semanticId}.centre`, semanticId, point(cursor + width / 2, m.stripY + m.stripH / 2));
    builder.hit(semanticId, box(cursor, m.stripY, Math.max(width, 6), m.stripH));
    builder.relation(`${semanticId}.share`, 'part-of', semanticId, PROPORTION_WHOLE_ID, `${part.label} is ${format(part.value)} of ${format(spec.total)} ${spec.unit}`);
    cursor += width;
  });

  if (magnified.length) {
    const first = spans.get(magnified[0]!)!;
    const last = spans.get(magnified[magnified.length - 1]!)!;
    const windowX = first.x;
    const windowEnd = last.x + last.width;
    const detailX = m.stripX;
    const detailW = m.stripW;

    // The magnifier: two construction lines from the exact slice to its enlargement.
    builder.line(`${PROPORTION_MAGNIFIER_ID}.tick.start`, PROPORTION_MAGNIFIER_ID, 'construction', point(windowX, m.stripY + m.stripH), point(windowX, m.bracketY));
    builder.line(`${PROPORTION_MAGNIFIER_ID}.tick.end`, PROPORTION_MAGNIFIER_ID, 'construction', point(windowEnd, m.stripY + m.stripH), point(windowEnd, m.bracketY));
    builder.path(`${PROPORTION_MAGNIFIER_ID}.taper.left`, PROPORTION_MAGNIFIER_ID, 'construction',
      `M ${d(windowX)} ${d(m.bracketY)} L ${d(detailX)} ${d(m.detailTop)}`, { dash: '3 4' });
    builder.path(`${PROPORTION_MAGNIFIER_ID}.taper.right`, PROPORTION_MAGNIFIER_ID, 'construction',
      `M ${d(windowEnd)} ${d(m.bracketY)} L ${d(detailX + detailW)} ${d(m.detailTop)}`, { dash: '3 4' });
    // The magnification statement sits above the taper, clear of both strips.
    const factorText = `${format(magnifiedValue)} ${spec.unit} OF THE WHOLE, SHOWN ${Math.round(factor)}× LARGER`;
    const factorShort = `${format(magnifiedValue)} ${spec.unit} SHOWN ${Math.round(factor)}× LARGER`;
    const chosen = fits(factorText, TYPE.micro, windowX - m.stripX - 14, true, true) ? factorText : factorShort;
    builder.text(`${PROPORTION_MAGNIFIER_ID}.factor`, PROPORTION_MAGNIFIER_ID, 'annotation', point(Math.max(m.stripX + textWidth(chosen, TYPE.micro, true, true), windowX - 10), m.bracketY - 8), chosen, {
      size: TYPE.micro,
      mono: true,
      weight: 700,
      align: 'end',
      uppercase: true,
    });
    builder.anchor(`${PROPORTION_MAGNIFIER_ID}.window`, PROPORTION_MAGNIFIER_ID, point(windowX, m.stripY + m.stripH), 'down');
    builder.hit(PROPORTION_MAGNIFIER_ID, box(Math.max(0, windowX - 8), m.stripY + m.stripH, Math.max(20, windowEnd - windowX + 16), m.bracketY - m.stripY - m.stripH + 20));

    builder.rect(`${PROPORTION_MAGNIFIER_ID}.frame`, PROPORTION_MAGNIFIER_ID, 'structure', box(detailX, m.detailTop, detailW, m.detailH), { radius: 3 });
    let detailCursor = detailX;
    magnified.forEach((partId, index) => {
      const part = spec.parts.find((candidate) => candidate.id === partId)!;
      const semanticId = `${proportionPartId(part.id)}.magnified`;
      const width = (part.value / magnifiedValue) * detailW;
      const active = part.id === spec.focusPartId;
      builder.rect(`${semanticId}.band`, semanticId, active ? 'active' : index % 2 === 0 ? 'record' : 'surface', box(detailCursor, m.detailTop, width, m.detailH), {
        label: `${part.label}: ${format(part.value)} ${spec.unit} of the whole`,
        interactive: true,
      });
      if (index > 0) builder.line(`${semanticId}.edge`, semanticId, 'structure', point(detailCursor, m.detailTop), point(detailCursor, m.detailTop + m.detailH));
      const value = part.amount ?? `${format(part.value)} ${spec.unit}`;
      if (fits(part.label, TYPE.label, width - 14, true) && fits(value, TYPE.micro, width - 14, true)) {
        builder.text(`${semanticId}.label`, semanticId, 'structure', point(detailCursor + width / 2, m.detailTop + 22), part.label, {
          size: TYPE.label, mono: true, weight: 700, align: 'middle',
        });
        builder.text(`${semanticId}.share`, semanticId, 'annotation', point(detailCursor + width / 2, m.detailTop + 38), value, {
          size: TYPE.micro, mono: true, weight: 500, align: 'middle',
        });
      } else {
        // Still too narrow even magnified: label it below with a leader, never inside.
        const tipX = detailCursor + width / 2;
        const labelText = `${part.label} · ${value}`;
        builder.line(`${semanticId}.leader`, semanticId, 'annotation', point(tipX, m.detailTop + m.detailH), point(tipX, m.detailLabelY - 10));
        builder.text(`${semanticId}.label`, semanticId, 'structure', point(clampLabelX(tipX, labelText, m.stripX, m.stripW), m.detailLabelY), labelText, {
          size: TYPE.micro, mono: true, weight: 700, align: 'end',
        });
      }
      builder.anchor(`${semanticId}.centre`, semanticId, point(detailCursor + width / 2, m.detailTop + m.detailH / 2));
      builder.hit(semanticId, box(detailCursor, m.detailTop, Math.max(width, 6), m.detailH));
      builder.relation(`${semanticId}.magnifies`, 'magnifies', semanticId, proportionPartId(part.id), `${part.label} shown ${Math.round(factor)}× larger than on the strip`);
      detailCursor += width;
    });
  }

  const notes = [
    `${spec.parts.length} PARTS · SUM ${format(spec.total)} ${spec.unit} · NOTHING ROUNDED UP TO BE SEEN`,
    `${spec.parts.length} PARTS · NOTHING ROUNDED UP TO BE SEEN`,
    `${spec.parts.length} PARTS · NOTHING ROUNDED UP`,
  ];
  note(builder, 'proportion.note', 'proportion', point(m.stripX, m.footerY), notes.find((candidate) => fits(candidate, TYPE.micro, frame.contentWidth, true, true)) ?? notes[notes.length - 1]!);

  return finishPrimitive(spec, builder, {
    title: spec.title,
    subtitle: spec.subtitle,
    state: spec.focusPartId ? `focus:${spec.focusPartId}` : 'overview',
    active: spec.focusPartId
      ? [proportionPartId(spec.focusPartId), `${proportionPartId(spec.focusPartId)}.magnified`, ...(spec.emphasis ?? [])]
      : [PROPORTION_WHOLE_ID, ...(spec.emphasis ?? [])],
    warnings,
  });
}

export function validateProportionStrip(spec: ProportionStripSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.family !== 'proportion-strip' || spec.view !== 'orthographic') throw new Error('proportion-strip: family and view are fixed');
  if (!spec.provenance.trim()) throw new Error('proportion-strip: provenance is required');
  if (!spec.subject.trim() || !spec.unit.trim() || !spec.title.trim() || !spec.subtitle.trim()) throw new Error('proportion-strip: subject, unit, title and subtitle are required');
  if (spec.parts.length < 2 || spec.parts.length > 7) throw new Error('proportion-strip: require 2…7 parts');
  const ids = new Set<string>();
  let sum = 0;
  for (const part of spec.parts) {
    if (!/^[a-z][a-z0-9-]*$/.test(part.id)) throw new Error(`proportion-strip: invalid part id ${part.id}`);
    if (ids.has(part.id)) throw new Error(`proportion-strip: duplicate part id ${part.id}`);
    ids.add(part.id);
    if (!part.label.trim() || part.label.length > 18) throw new Error(`proportion-strip: part ${part.id} needs a label of 1…18 characters`);
    if (!Number.isFinite(part.value) || part.value <= 0) throw new Error(`proportion-strip: part ${part.id} needs a positive share`);
    sum += part.value;
  }
  if (Math.abs(sum - spec.total) > 1e-9) {
    throw new Error(`proportion-strip: the parts sum to ${sum}, which is not the declared whole of ${spec.total}`);
  }
  const magnified = spec.magnifyPartIds ?? [];
  if (magnified.length) {
    if (magnified.length > 4) throw new Error('proportion-strip: magnify at most four parts');
    const indices = magnified.map((id) => spec.parts.findIndex((part) => part.id === id));
    if (indices.some((index) => index < 0)) throw new Error('proportion-strip: the magnifier names a part that does not exist');
    for (let step = 1; step < indices.length; step += 1) {
      if (indices[step] !== indices[step - 1]! + 1) throw new Error('proportion-strip: the magnifier must cover contiguous parts');
    }
    if (indices[indices.length - 1] !== spec.parts.length - 1) throw new Error('proportion-strip: the magnifier must reach the end of the strip');
    const magnifiedValue = magnified.reduce((total, id) => total + spec.parts.find((part) => part.id === id)!.value, 0);
    if (magnifiedValue >= spec.total) throw new Error('proportion-strip: magnifying the whole strip magnifies nothing');
  }
  if (spec.focusPartId && !ids.has(spec.focusPartId)) throw new Error('proportion-strip: the focus part does not exist');
  const required = metrics(spec).required;
  if (spec.viewport.height < required) throw new Error(`proportion-strip: this strip needs at least ${Math.ceil(required)}px of height`);
}

function clampLabelX(x: number, text: string, stripX: number, stripW: number): number {
  return Math.min(stripX + stripW, Math.max(stripX + textWidth(text, TYPE.micro, true), x + 30));
}

function format(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return String(Math.round(value * 1000) / 1000);
}
